import { Mill } from './types';
import millsData from '../data/placeholder-mills.json';

const mills: Mill[] = millsData as Mill[];

export const getAllMills = (): Mill[] =>
  [...mills].sort((a, b) =>
    a.state.localeCompare(b.state) || a.name.localeCompare(b.name)
  );

export const getMillById = (id: string): Mill | null =>
  mills.find((m) => m.id === id) ?? null;

export const getMillByCode = (code: string): Mill | null =>
  mills.find((m) => m.metadata?.code === code) ?? null;

// Verified location-context fields surfaced on the explorer-detail summary
// card. Each field is sourced from public USFS / EPA / state-forestry
// documents (see scripts/research notes / commit history); rewriting any
// of these in placeholder-mills.json should be paired with a quick
// re-verification against authoritative sources so the dashboard doesn't
// drift into uncited claims at conference time.
export interface MillBrief {
  /** Formal ecoregion name (typically EPA Level III), e.g. "Sierra Nevada (EPA III #5)". */
  ecoregion?: string;
  /** 2–4 ecologically/commercially dominant species in plain common-name form. */
  dominantSpecies?: string[];
  /** 3–5 word characterisation of the regional forest economy. */
  forestEconomyCharacter?: string;
  /** One additional verified fact worth surfacing — e.g. a recent fire,
   *  a flagship landmark, or a named protected area. Optional. */
  optionalNote?: string;
}

export const getMillBrief = (mill: Mill): MillBrief => {
  const meta = mill.metadata ?? {};
  return {
    ecoregion: typeof meta.ecoregion === 'string' ? meta.ecoregion : undefined,
    dominantSpecies: Array.isArray(meta.dominantSpecies)
      ? (meta.dominantSpecies as string[])
      : undefined,
    forestEconomyCharacter:
      typeof meta.forestEconomyCharacter === 'string'
        ? meta.forestEconomyCharacter
        : undefined,
    optionalNote: typeof meta.optionalNote === 'string' ? meta.optionalNote : undefined,
  };
};

export const hasMillBrief = (brief: MillBrief): boolean =>
  Boolean(
    brief.ecoregion ||
      (brief.dominantSpecies && brief.dominantSpecies.length > 0) ||
      brief.forestEconomyCharacter,
  );
